/**
 * 시연용 배치 OCR job 상세
 */
import { demoOcrService } from '@/entities/demo/api/demo-ocr.api';
import { removeStoredBatchJobId } from '@/pages/demo/utils/demoDocumentAiBatchStorage';
import { deleteDocumentAiBatchJobById } from '@/pages/demo/utils/documentAiBatchJobDelete';
import { formatBatchJobDateTime } from '@/pages/demo/utils/formatBatchJobDateTime';
import { extractOcrDataRows } from '@/pages/demo/utils/demoOcrResultModel';

import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { useToast } from '@/shared/utils/toast';
import { useSearch } from '@tanstack/react-router';
import { Loader2, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface BatchJobDetailSearch {
  jobId?: string;
}

export const DemoBatchJobDetail = () => {
  const { jobId = '' } = useSearch({ strict: false }) as BatchJobDetailSearch;
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; status: number | null; body: unknown } | null>(
    null,
  );

  const fetchJob = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    try {
      const res = await demoOcrService.fetchBatchJob(jobId);
      setResult(res);
      if (!res.ok) toast.error(`배치 job 조회 실패 (${res.status})`);
    } catch {
      toast.error('배치 job 조회 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [jobId, toast]);

  useEffect(() => {
    void fetchJob();
  }, [fetchJob]);

  const body = (result?.body ?? {}) as Record<string, unknown>;
  const items = useMemo(() => {
    const raw = body.items ?? body.images ?? body.results;
    return Array.isArray(raw) ? (raw as Record<string, unknown>[]) : [];
  }, [body]);

  const handleDelete = async () => {
    if (!jobId) return;
    setDeleting(true);
    try {
      const res = await deleteDocumentAiBatchJobById(jobId);
      if (res.success) {
        removeStoredBatchJobId(jobId);
        toast.success('배치 job이 삭제되었습니다.');
      } else if (res.notFound) {
        removeStoredBatchJobId(jobId);
        toast.error('이미 삭제되었거나 존재하지 않는 job입니다.');
      } else {
        toast.error('배치 job 삭제에 실패했습니다.');
      }
    } catch {
      toast.error('배치 job 삭제 중 오류가 발생했습니다.');
    } finally {
      setDeleting(false);
    }
  };

  if (!jobId) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          jobId 쿼리 파라미터가 필요합니다.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => void fetchJob()} disabled={loading}>
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          새로고침
        </Button>
        <Button variant="destructive" size="sm" onClick={() => void handleDelete()} disabled={deleting}>
          {deleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
          삭제
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="font-mono text-base">{jobId}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-2 text-sm md:grid-cols-3">
          <div>
            <span className="text-muted-foreground">상태 </span>
            <Badge variant="outline">{String(body.status ?? '—')}</Badge>
          </div>
          <div>
            <span className="text-muted-foreground">생성 </span>
            {formatBatchJobDateTime(body.createdAt)}
          </div>
          <div>
            <span className="text-muted-foreground">갱신 </span>
            {formatBatchJobDateTime(body.lastUpdatedAt ?? body.updatedAt)}
          </div>
        </CardContent>
      </Card>

      {items.map((item, imgIndex) => {
        const drugs = item.drugs ?? item.ocrResult ?? item.items;
        const rows = extractOcrDataRows(drugs);
        const imageUrl = String(item.imageUrl ?? '').trim();
        const imageName = String(item.imageName ?? item.fileName ?? `이미지 ${imgIndex + 1}`);
        return (
          <Card key={`${jobId}-${imgIndex}`}>
            <CardHeader>
              <CardTitle className="text-sm">{imageName}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {imageUrl ? (
                <img src={imageUrl} alt={imageName} className="max-h-64 rounded border object-contain" />
              ) : null}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>코드</TableHead>
                    <TableHead>품명</TableHead>
                    <TableHead>원내</TableHead>
                    <TableHead>원외</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground">
                        OCR 행 없음
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>{String(row.drugCode ?? row.code ?? '—')}</TableCell>
                        <TableCell>{String(row.drugName ?? row.name ?? '—')}</TableCell>
                        <TableCell>{String(row.inHospitalQuantity ?? row.inHouseUsageAmount ?? '—')}</TableCell>
                        <TableCell>{String(row.outHospitalQuantity ?? row.outpatientUsageAmount ?? '—')}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        );
      })}

      {items.length === 0 && !loading && (
        <Card>
          <CardContent className="py-6 text-center text-muted-foreground">
            이미지 항목이 없거나 아직 처리 중입니다.
          </CardContent>
        </Card>
      )}
    </div>
  );
};
